import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Copy, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { usePASClientAuth } from '../../context/PASClientAuthContext';
import { SectionGuide } from '../../components/pas/SectionGuide';
import { WithdrawForm } from '../../components/pas/BankWithdrawForm';
import { SwapStatus } from '../../components/pas/SwapForm';
import { BankStatement } from '../../components/pas/BankStatement';
import { AddressPrompt } from '../../components/pas/AddressPrompt';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-bank`;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Num = ({ label, value, show, testid }) => (
  <div className="bg-slate-800/60 rounded-lg p-3">
    <p className="text-[11px] text-slate-400">{label}</p>
    <p className="font-mono text-white flex items-center gap-2" data-testid={testid}>{show ? value : `••••${value.slice(-4)}`}
      <button onClick={() => { navigator.clipboard?.writeText(value).then(() => toast.success(`${label} copied`)).catch(() => toast.error('Copy not allowed by this browser')); }} className="text-slate-400 hover:text-white" data-testid={`${testid}-copy`}><Copy className="w-3.5 h-3.5" /></button></p>
  </div>
);

const AccountCard = ({ d }) => {
  const [show, setShow] = useState(false);
  const a = d.account;
  return (
    <div className="bg-slate-900 rounded-2xl p-5 border border-emerald-500/30 space-y-4" data-testid="bank-account-card">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold flex items-center gap-2"><Building2 className="w-5 h-5 text-emerald-400" /> BETHCOIN bank account {a.status === 'frozen' && <span className="text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded" data-testid="bank-frozen-badge">Frozen</span>}</h2>
        <button onClick={() => setShow(!show)} className="text-xs text-slate-400 hover:text-white flex items-center gap-1" data-testid="bank-show-numbers">{show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {show ? 'Hide' : 'Show'} numbers</button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Num label="Account number" value={a.account_number} show={show} testid="bank-account-number" />
        <Num label="Routing number" value={a.routing_number} show testid="bank-routing-number" />
      </div>
      <div className="grid grid-cols-2 gap-3 text-center">
        <div className="bg-slate-800/60 rounded-lg p-3"><p className="text-[11px] text-slate-400">Bank balance</p><p className="text-xl font-bold text-emerald-300" data-testid="bank-balance">{money(a.bank_balance)}</p></div>
        <div className="bg-slate-800/60 rounded-lg p-3"><p className="text-[11px] text-slate-400">BETH USD on Base</p><p className="text-xl font-bold" data-testid="bank-beth-balance">{money(a.beth_balance)}</p></div>
      </div>
      <p className="text-xs text-slate-500">Your bank balance mirrors your BETH USD 1:1 and updates automatically.</p>
    </div>
  );
};

const Txs = ({ rows }) => (
  <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800" data-testid="bank-transactions">
    <h3 className="font-semibold mb-3">Bank transactions</h3>
    {!rows.length ? <p className="text-xs text-slate-500">No transactions yet.</p> : rows.map(t => (
      <div key={t.id} className="flex justify-between text-sm py-1.5 border-b border-slate-800" data-testid={`bank-tx-${t.id}`}>
        <span className="text-slate-300">{t.date} · {t.desc || t.type_source}</span>
        <span className={String(t.amount).startsWith('-') ? 'text-red-300' : 'text-emerald-300'}>{money(t.amount)}</span>
      </div>
    ))}
  </div>
);

export default function PASBankAccount() {
  const { token } = usePASClientAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stmtKey, setStmtKey] = useState(0);
  const [searchParams] = useSearchParams();
  const load = useCallback(() => {
    axios.get(`${API}/me`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setD(r.data)).catch(e => toast.error(e.response?.data?.detail || 'Could not load bank account'));
  }, [token]);
  useEffect(() => { if (!token) navigate('/pas/login'); else load(); }, [token, navigate, load]);

  const withdraw = async (body, reset) => {
    setBusy(true);
    try { const r = await axios.post(`${API}/withdraw`, body, { headers: { Authorization: `Bearer ${token}` } }); toast.success(r.data.message); reset(); load(); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Withdrawal failed'); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-8" data-testid="bank-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/pas/dashboard')} className="text-slate-400 text-sm flex items-center gap-1" data-testid="bank-back-btn"><ArrowLeft className="w-4 h-4" /> Dashboard</button>
          <button onClick={load} className="text-slate-400 text-sm flex items-center gap-1 hover:text-white" data-testid="bank-refresh-btn"><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>
        <h1 className="text-3xl font-bold flex items-center gap-3"><Building2 className="w-8 h-8 text-emerald-400" /> Bank Account</h1>
        <SectionGuide id="bank" />
        <AddressPrompt token={token} prefix="bank-address" onSaved={() => setStmtKey(k => k + 1)} />
        {!d ? <p className="text-slate-400">Loading…</p> : !d.has_account ? (
          <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800" data-testid="bank-no-account"><p className="text-slate-300">Your bank account hasn't been opened yet. The BETHCOIN administrator opens accounts after review; you'll see your account and routing number here once it's ready.</p></div>
        ) : (
          <>
            <AccountCard d={d} />
            <BankStatement key={stmtKey} token={token} initialMonth={searchParams.get('month')} />
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800"><h3 className="font-semibold mb-1">Withdraw to another bank</h3><p className="text-xs text-slate-400 mb-3">Available to withdraw: <span className="text-white font-semibold" data-testid="bank-available-withdraw">{money(d.available_to_withdraw)}</span></p><WithdrawForm feePct={d.fee_pct} busy={busy} onSubmit={withdraw} /></div>
              <Txs rows={d.transactions} />
            </div>
          </>
        )}
        {d?.withdrawals?.length > 0 && (
          <div data-testid="bank-withdrawals">
            <p className="text-sm text-slate-300 font-medium mb-2">My withdrawals</p>
            {d.withdrawals.map(w => (
              <div key={w.id} className="flex flex-wrap justify-between gap-2 py-2 border-b border-slate-800 text-sm" data-testid={`bank-wd-${w.id}`}>
                <span>{w.method.toUpperCase()} {money(w.net)} to {w.recipient.name_on_account} ••••{w.recipient.account_last4}</span>
                <SwapStatus s={w.status} />
                {(w.error || w.reason) && <p className="w-full text-xs text-red-400">{w.error || w.reason}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
